import React from 'react';
import _ from 'lodash';

import {
  ApolloClient,
  InMemoryCache,
  split,
  HttpLink,
  ApolloLink,
  TypePolicies
} from '@apollo/client';
import {
  ContextSetter,
  setContext
} from '@apollo/client/link/context';
import {
  authUtil,
  SystemAlert,
  shopifyAuthUtil
} from 'modules/layer00';

import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';



// Track if WordPress auth has been initialized to avoid multiple initializations
let __shopifyAuthInitialized = false;

export function useAuthAwareApolloClient() {
  const inMemoryCache = React.useMemo(
    () => new InMemoryCache({
      typePolicies
    }),
    []
  );

  // Initialize WordPress auth synchronously on first render
  // This ensures the reactive variable is populated before any GraphQL requests
  if (!__shopifyAuthInitialized) {
    shopifyAuthUtil.initializeShopifyAuthFromURL();
    __shopifyAuthInitialized = true;
  }

  const authState = authUtil.useAuthState({
    onLogOut: () => {
      inMemoryCache.reset();
    }
  });



  const setContextCallback: ContextSetter = React.useCallback(
    (operation, prevContext) => {
      const headers: any = {
        ...prevContext.headers
      };

      if (authState.accessToken) {
        headers.authorization = `Bearer ${authState.accessToken}`;
      }

      // Shopify customer authentication
      // Read directly from reactive variable to get latest state (can't use hook in callback)
      const currentShopifyAuthState = shopifyAuthUtil.getShopifyAuthState();

      if (currentShopifyAuthState.isValid && currentShopifyAuthState.token && currentShopifyAuthState.timestamp) {
        headers['x-shopify-token'] = currentShopifyAuthState.token;
        headers['x-shopify-timestamp'] = currentShopifyAuthState.timestamp;
        headers['x-shopify-email'] = currentShopifyAuthState.email;
      }

      return {
        headers
      };
    },
    [authState.accessToken]
  );

  const httpLink = React.useMemo(
    () => new HttpLink({
      uri: process.env.REACT_APP_API_GRAPHQL_URL
    }),
    []
  );

  const authHttpLink = React.useMemo(
    () => {
      const link = ApolloLink.from([
        setContext(setContextCallback),
        httpLink
      ]);

      return link;
    },
    [
      setContextCallback,
      httpLink
    ]
  );

  const wsLink = React.useMemo(() => new GraphQLWsLink(
    createClient({
      url: 'ws://localhost:4000/graphql'
    })
  ), []);

  const splitLink = React.useMemo(
    () => split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        );
      },
      wsLink,
      authHttpLink
    ),
    [
      authHttpLink,
      wsLink
    ]);

  const errorLink = React.useMemo(
    () => onError(({ graphQLErrors, networkError }) => {
      const errs = _.uniqBy(graphQLErrors, err => err.originalError?.message);

      if (networkError) {
        SystemAlert.sendAlert({
          severity: 'error',
          message: networkError.message
        });
      }

      errs.forEach(err =>
        SystemAlert.sendAlert({
          severity: 'error',
          message: err.message
        }));
    }),
    []
  );

  return React.useMemo(
    () => new ApolloClient({
      cache: inMemoryCache,
      connectToDevTools: true,
      link: ApolloLink.from([
        errorLink,
        splitLink
      ])
    }),
    [
      errorLink,
      inMemoryCache,
      splitLink
    ]);
}



const typePolicies: TypePolicies = {};
